import { useState } from 'react';
import { Product } from '../page';
import { CartItem } from './Cart';
import { useAuth } from '../../../contexts/AuthContext';

interface ProductCardProps {
  product: Product;
  onEdit: () => void;
  onView: () => void;
  onDelete: () => void;
  onAddToCart: (item: Omit<CartItem, 'id'>) => void;
  showActions?: boolean;
}

export default function ProductCard({
  product,
  onEdit,
  onView,
  onDelete,
  onAddToCart,
  showActions = true
}: ProductCardProps) {
  const [showAddToCart, setShowAddToCart] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const { userProfile } = useAuth();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const getTotalQuantity = () => {
    return product.variants.reduce((total, variant) => total + variant.quantity, 0);
  };

  const getDiscountedPrice = (sellingPrice: number) => {
    if (userProfile?.role === 'partner' && userProfile?.commissionRate) {
      return sellingPrice * (100 - userProfile.commissionRate) / 100;
    }
    return sellingPrice;
  };

  const getDisplayPrice = () => {
    return userProfile?.role === 'partner' 
      ? getDiscountedPrice(product.sellingPrice)
      : product.sellingPrice;
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      alert('Vui lòng chọn màu sắc và kích thước!');
      return;
    }

    if (quantity > selectedVariant.quantity) {
      alert(`Số lượng tối đa cho sản phẩm này là ${selectedVariant.quantity}`);
      return;
    }

    const cartItem: Omit<CartItem, 'id'> = {
      productId: product.id,
      productName: product.name,
      color: selectedVariant.color,
      size: selectedVariant.size,
      quantity: quantity,
      price: getDisplayPrice(),
      image: selectedVariant.images?.[0] || product.image,
      maxStock: selectedVariant.quantity
    };

    onAddToCart(cartItem);
    setShowAddToCart(false);
    setSelectedVariant(null);
    setQuantity(1);
    
    alert(`Đã thêm ${quantity} sản phẩm vào giỏ hàng!`);
  };

  const AddToCartModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">Thêm vào giỏ hàng</h3>
          <button
            onClick={() => {
              setShowAddToCart(false);
              setSelectedVariant(null);
              setQuantity(1);
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Product Info */}
          <div className="flex items-start gap-4 mb-6">
            <img
              src={product.image}
              alt={product.name}
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">{product.name}</h4>
              <p className="text-sm text-gray-600 mb-2">{product.category}</p>
              <p className="text-lg font-bold text-orange-600">
                {formatCurrency(getDisplayPrice())}
              </p>
              {userProfile?.role === 'partner' && userProfile?.commissionRate && (
                <p className="text-xs text-gray-500">
                  Giá gốc: {formatCurrency(product.sellingPrice)} 
                  <span className="text-orange-600 ml-1">
                    (-{userProfile.commissionRate}%)
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Variant Selection */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Chọn màu sắc và kích thước *
              </label>
              {product.variants.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <i className="ri-alert-line text-2xl mb-2"></i>
                  <p>Sản phẩm này chưa có biến thể</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {product.variants.map((variant, index) => (
                    <div
                      key={`${variant.color}-${variant.size}-${index}`}
                      onClick={() => {
                        if (variant.quantity > 0) {
                          setSelectedVariant(variant);
                          setQuantity(1);
                        }
                      }}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        variant.quantity === 0
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                          : selectedVariant?.color === variant.color && selectedVariant?.size === variant.size
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {variant.color} - {variant.size}
                          </p>
                          <p className={`text-sm ${
                            variant.quantity === 0 ? 'text-red-500' : 'text-gray-600'
                          }`}>
                            {variant.quantity === 0 
                              ? 'Hết hàng' 
                              : `Tồn kho: ${variant.quantity} sản phẩm`
                            }
                          </p>
                        </div>
                        {variant.images && variant.images[0] && (
                          <img
                            src={variant.images[0]}
                            alt={`${product.name} ${variant.color}`}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quantity Selection */}
            {selectedVariant && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số lượng
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                      setQuantity(Math.min(newQuantity, selectedVariant.quantity));
                    }}
                    min="1"
                    max={selectedVariant.quantity}
                    className="w-20 text-center border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(selectedVariant.quantity, quantity + 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Tối đa: {selectedVariant.quantity} sản phẩm
                </p>
              </div>
            )}

            {/* Total Price */}
            {selectedVariant && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Thành tiền:</span>
                  <span className="text-xl font-bold text-orange-600">
                    {formatCurrency(getDisplayPrice() * quantity)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowAddToCart(false);
                setSelectedVariant(null);
                setQuantity(1);
              }}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
            >
              Hủy bỏ
            </button>
            <button
              onClick={handleAddToCart}
              disabled={!selectedVariant || selectedVariant.quantity === 0}
              className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Thêm vào giỏ
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
        <div className="relative">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-64 object-cover cursor-pointer"
            onClick={onView}
          />
          {getTotalQuantity() === 0 && (
            <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              Hết hàng
            </div>
          )}
          {userProfile?.role === 'partner' && userProfile?.commissionRate && (
            <div className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              -{userProfile.commissionRate}%
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="mb-3">
            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 cursor-pointer hover:text-orange-600 transition-colors" onClick={onView}>
              {product.name}
            </h3>
            <p className="text-sm text-gray-600">{product.category}</p>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-lg font-bold text-orange-600">
                {formatCurrency(getDisplayPrice())}
              </span>
              {userProfile?.role === 'partner' && userProfile?.commissionRate && (
                <span className="text-sm text-gray-500 line-through">
                  {formatCurrency(product.sellingPrice)}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Tồn kho: <span className="font-medium">{getTotalQuantity()}</span> sản phẩm
            </p>
          </div>

          <div className="space-y-3">
            {/* Add to Cart Button */}
            <button
              onClick={() => setShowAddToCart(true)}
              disabled={getTotalQuantity() === 0}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line"></i>
              {getTotalQuantity() === 0 ? 'Hết hàng' : 'Thêm vào giỏ'}
            </button>

            {/* Action Buttons for Manager */}
            {showActions && userProfile?.role !== 'partner' && (
              <div className="flex gap-2">
                <button
                  onClick={onView}
                  className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-eye-line text-sm"></i>
                  <span className="text-sm">Xem</span>
                </button>
                <button
                  onClick={onEdit}
                  className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-edit-line text-sm"></i>
                  <span className="text-sm">Sửa</span>
                </button>
                <button
                  onClick={onDelete}
                  className="flex-1 border border-red-300 text-red-600 hover:bg-red-50 py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                  <span className="text-sm">Xóa</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      {showAddToCart && <AddToCartModal />}
    </>
  );
}